import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ShoppingBag } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { API } from '../lib/shop';
import { useShopConfig } from '../contexts/ShopConfigContext';
import ShopComingSoon from './ShopComingSoon';

export default function Shop() {
  const config = useShopConfig();
  if (config.loading) return <div className="min-h-screen pt-32 text-center text-gray-500" data-testid="shop-loading">Loading...</div>;
  if (!config.live) return <ShopComingSoon />;
  return <ShopCatalogue />;
}

function ShopCatalogue() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState('All');

  useEffect(() => {
    axios.get(`${API}/shop/products`)
      .then(r => setProducts(r.data))
      .catch(() => setError('Could not load products. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))], [products]);
  const visible = category === 'All' ? products : products.filter(p => p.category === category);

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gradient-to-b from-pink-50 to-white" data-testid="shop-page">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mb-12">
          <p className="text-pink-600 font-semibold uppercase tracking-wider text-sm mb-3 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" /> Clean Home Shop
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
            Professional cleaning products, <span className="text-pink-600">delivered</span>
          </h1>
          <p className="text-base md:text-lg text-gray-600">
            The same products our team uses every day, plus gift vouchers for any Clean Home service. Secure card payments, delivered across Peterborough and the UK.
          </p>
        </div>

        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="shop-category-filter">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`shop-category-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors ${category === c ? 'bg-pink-500 border-pink-500 text-white' : 'bg-white border-pink-100 text-gray-700 hover:border-pink-300'}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading && <p className="text-gray-600" data-testid="catalogue-loading">Loading products...</p>}
        {error && <p className="text-red-600" data-testid="shop-error">{error}</p>}
        {!loading && !error && visible.length === 0 && (
          <p className="text-gray-600" data-testid="shop-empty">No products available right now. Please check back soon.</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
          {visible.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </div>
  );
}
